/* ============================================================
   CardioViz – framework-free SVG building blocks for explaining
   cardiac biomarker calculations (TypeScript port of cardioviz.html).

   Layers: Anim (time) -> components (state + SVG) -> overlay (links)
   Components keep their data in plain objects and only touch the DOM
   in render()/paint(), so Vue wrappers can drive them from watchers.
   ============================================================ */

export const NS = 'http://www.w3.org/2000/svg';
export const RAD = Math.PI / 180;
export const ACCENT = '#00bcd4';

type Attrs = Record<string, string | number>;

export function svgEl<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Attrs = {},
  parent?: Element,
): SVGElementTagNameMap[K] {
  const e = document.createElementNS(NS, name);
  for (const k in attrs) e.setAttribute(k, String(attrs[k]));
  if (parent) parent.append(e);
  return e;
}

export const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

function parseColor(c: string): number[] {
  if (c[0] === '#') {
    if (c.length === 4) c = `#${[...c.slice(1)].map(x => x + x).join('')}`;
    return [1, 3, 5].map(i => Number.parseInt(c.substr(i, 2), 16));
  }
  return (c.match(/[\d.]+/g) || ['0', '0', '0']).slice(0, 3).map(Number);
}

/** Linear RGB mix between two colors (hex or rgb()). */
export function mix(a: string, b: string, t: number): string {
  const x = parseColor(a);
  const y = parseColor(b);
  return `rgb(${x.map((v, i) => Math.round(v + (y[i] - v) * t)).join(',')})`;
}

export function luminance(c: string): number {
  const [r, g, b] = parseColor(c);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/** Color scale from color stops: scale(['#fff','#00f'])(0..1) */
export const scale = (stops: string[]) => (t: number) => {
  t = clamp(t) * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(t));
  return mix(stops[i], stops[i + 1], t - i);
};

type Handler = (data: any) => void;
export interface Emitter {
  on: (e: string, f: Handler) => () => void;
  off: (e: string, f: Handler) => void;
  emit: (e: string, d?: any) => void;
}
function emitter<T extends object>(o: T): T & Emitter {
  const m: Record<string, Handler[]> = {};
  const out = o as T & Emitter;
  out.on = (e, f) => {
    (m[e] ??= []).push(f);
    return () => out.off(e, f);
  };
  out.off = (e, f) => {
    m[e] = (m[e] || []).filter(x => x !== f);
  };
  out.emit = (e, d) => (m[e] || []).forEach(f => f(d));
  return out;
}

/* ---------- Animation engine: one rAF loop, time-based, cancellable by owner+key ---------- */
export type EaseName = 'linear' | 'in' | 'out' | 'inOut';
export interface TweenOptions {
  duration?: number;
  delay?: number;
  ease?: EaseName;
  onUpdate?: (p: number) => void;
  /** A new tween with the same owner+key cancels (and resolves) the previous one. */
  owner?: object;
  key?: string;
}

interface ActiveTween {
  t0: number | null;
  delay: number;
  duration: number;
  ease: EaseName;
  fn: (p: number) => void;
  done: () => void;
}

export const Anim = (() => {
  const E: Record<EaseName, (t: number) => number> = {
    linear: t => t,
    in: t => t ** 3,
    out: t => 1 - (1 - t) ** 3,
    inOut: t => (t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2),
  };
  const active = new Set<ActiveTween>();
  const subs = new Set<() => void>();
  const owners = new WeakMap<object, Map<string, () => void>>();
  const reduce
    = typeof matchMedia !== 'undefined'
      && matchMedia('(prefers-reduced-motion: reduce)').matches;
  let raf = 0;
  let speed = 1;

  function tick(now: number) {
    for (const a of [...active]) {
      a.t0 ??= now + a.delay;
      const p = clamp((now - a.t0) / a.duration);
      a.fn(E[a.ease](p));
      if (p >= 1) {
        active.delete(a);
        a.done();
      }
    }
    subs.forEach(f => f());
    raf = active.size ? requestAnimationFrame(tick) : 0;
  }

  async function tween(o: TweenOptions): Promise<void> {
    let m: Map<string, () => void> | undefined;
    if (o.owner && o.key) {
      m = owners.get(o.owner);
      if (!m) {
        m = new Map();
        owners.set(o.owner, m);
      }
      m.get(o.key)?.();
    }
    if ((o.duration === 0 || (o.duration != null && o.duration <= 0)) && !o.delay) {
      if (m && o.key) m.delete(o.key);
      o.onUpdate?.(1);
      return Promise.resolve();
    }
    const instant = reduce || speed >= 1000;
    const a: ActiveTween = {
      t0: null,
      delay: instant ? 0 : (o.delay || 0) / speed,
      duration: instant ? 1 : Math.max(1, (o.duration ?? 600) / speed),
      ease: o.ease || 'inOut',
      fn: o.onUpdate || (() => {}),
      done: () => {},
    };
    const p = new Promise<void>((res) => {
      a.done = res;
      if (m && o.key) {
        m.set(o.key, () => {
          active.delete(a);
          res();
        });
      }
    });
    active.add(a);
    raf ||= requestAnimationFrame(tick);
    return p;
  }

  return {
    tween,
    wait: async (ms: number) => tween({ duration: ms, ease: 'linear' }),
    afterTick: (f: () => void) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    /** Global playback speed. >= 1000 makes every tween finish on the next frame (fast-forward). */
    setSpeed: (k: number) => {
      speed = Math.max(0.01, k);
    },
    getSpeed: () => speed,
    ease: E,
  };
})();

/* ---------- Myocardial ring ---------- */
const pt = (c: number, r: number, a: number): [number, number] => [
  c + r * Math.sin(a * RAD),
  c - r * Math.cos(a * RAD),
];

export function sectorPath(
  c: number,
  r0: number,
  r1: number,
  a0: number,
  a1: number,
): string {
  a1 = Math.max(a0, Math.min(a1, a0 + 359.9));
  const L = a1 - a0 > 180 ? 1 : 0;
  const [x0, y0] = pt(c, r1, a0);
  const [x1, y1] = pt(c, r1, a1);
  const [x2, y2] = pt(c, r0, a1);
  const [x3, y3] = pt(c, r0, a0);
  return `M${x0} ${y0}A${r1} ${r1} 0 ${L} 1 ${x1} ${y1}L${x2} ${y2}A${r0} ${r0} 0 ${L} 0 ${x3} ${y3}Z`;
}

export interface TerritoryInput {
  color?: string;
  inner?: string;
  outer?: string;
  opacity?: number;
  value?: number;
  label?: string;
}
interface TerritoryState {
  inner: string;
  outer: string;
  op: number;
  value: number;
  label: string;
}
type RingNumKey
  = | 'rotation'
    | 'outerR'
    | 'thickness'
    | 'gap'
    | 'innerFrac'
    | 'opacity'
    | 'scale';
const RING_NUM: RingNumKey[] = [
  'rotation',
  'outerR',
  'thickness',
  'gap',
  'innerFrac',
  'opacity',
  'scale',
];
type RingState = Record<RingNumKey, number> & { t: TerritoryState[] };
const cloneRing = (s: RingState): RingState => ({
  ...s,
  t: s.t.map(x => ({ ...x })),
});

export type RingLabelMode = 'none' | 'index' | 'value' | 'label';

export interface MyocardiumOptions {
  segments?: number;
  size?: number;
  outerR?: number;
  thickness?: number;
  gap?: number;
  innerFrac?: number;
  rotation?: number;
  labels?: RingLabelMode;
  fmt?: (v: number, i: number) => string | number;
  interactive?: boolean;
  dim?: number;
  accent?: string;
  base?: string;
  outline?: string;
  textColor?: string;
  territories?: TerritoryInput[];
  center?: string;
}

export interface AnimateOptions {
  duration?: number;
  ease?: EaseName;
  delay?: number;
  stagger?: number;
}

export type RingTarget = Partial<Record<RingNumKey, number>> & {
  segments?: number;
  labels?: RingLabelMode;
  territories?: TerritoryInput[];
};

export interface ScatterOptions {
  count?: number;
  /** Pixel colors are drawn from this function (default: random gray). */
  color?: (k: number) => string;
  appear?: number;
  hold?: number;
  collapse?: number;
  radius?: number;
}

export type Myocardium = ReturnType<typeof createMyocardium>;

/**
 * Segmented short-axis myocardial ring.
 * Methods: animateTo updateData rotate rotateTo highlightTerritory selectTerritory show hide
 *          scatter anchor getData getSelection setCenter on/off destroy.  Events: select, highlight
 */
export function createMyocardium(host: HTMLElement, o: MyocardiumOptions = {}) {
  const cfg = {
    segments: 16,
    size: 320,
    outerR: 132,
    thickness: 48,
    gap: 3,
    innerFrac: 0.5,
    rotation: 0,
    labels: 'none' as RingLabelMode,
    fmt: ((v: number) => v) as (v: number, i: number) => string | number,
    interactive: true,
    dim: 0.65,
    accent: ACCENT,
    base: '#dfe3ea',
    outline: '#1d1d1f',
    textColor: '#6e6e73',
    territories: [] as TerritoryInput[],
    center: '',
    ...o,
  };
  const api = emitter({}) as Emitter & {
    highlightTerritory: (
      i: number | number[] | null,
      o?: { source?: string; duration?: number },
    ) => Promise<void>;
    selectTerritory: (
      i: number | null,
      o?: { source?: string; duration?: number },
    ) => Promise<void>;
  };
  const c = cfg.size / 2;
  const svg = svgEl('svg', { viewBox: `0 0 ${cfg.size} ${cfg.size}`, class: 'cv-ring' });
  svg.style.cssText = 'display:block;width:100%;height:auto;overflow:visible';
  host.append(svg);
  const root = svgEl('g', {}, svg);
  const gS = svgEl('g', {}, root);
  const gO = svgEl('g', { 'pointer-events': 'none' }, root);
  const gL = svgEl('g', { 'pointer-events': 'none' }, root);
  const gX = svgEl('g', { 'pointer-events': 'none' }, root);
  const ctr = svgEl('text', {
    'x': c,
    'y': c,
    'text-anchor': 'middle',
    'dominant-baseline': 'central',
    'font-size': 15,
    'fill': cfg.textColor,
    'pointer-events': 'none',
  }, root);
  ctr.textContent = cfg.center;

  const defT = (): TerritoryState => ({
    inner: cfg.base,
    outer: cfg.base,
    op: 1,
    value: 0,
    label: '',
  });
  const norm = (p: TerritoryInput): Partial<TerritoryState> => {
    const r: Partial<TerritoryState> = {};
    if (p.color != null) r.inner = r.outer = p.color;
    if (p.inner != null) r.inner = p.inner;
    if (p.outer != null) r.outer = p.outer;
    if (p.opacity != null) r.op = p.opacity;
    if (p.value != null) r.value = p.value;
    if (p.label != null) r.label = p.label;
    return r;
  };

  interface SegEls {
    in: SVGPathElement;
    out: SVGPathElement;
    ol: SVGPathElement;
    tx: SVGTextElement;
  }
  let els: SegEls[] = [];
  let sel = -1;
  let hl = new Set<number>();
  let fo = { f: [] as number[], dim: 0 };
  let goalRot = cfg.rotation;

  function build(n: number) {
    [gS, gO, gL].forEach(g => g.replaceChildren());
    els = [];
    for (let i = 0; i < n; i++) {
      const g = svgEl('g', {}, gS);
      const e: SegEls = {
        in: svgEl('path', { 'stroke-width': 0.5 }, g),
        out: svgEl('path', { 'stroke-width': 0.5 }, g),
        ol: svgEl('path', { 'fill': 'none', 'stroke-linejoin': 'round' }, gO),
        tx: svgEl('text', { 'text-anchor': 'middle', 'dominant-baseline': 'central' }, gL),
      };
      if (cfg.interactive) {
        g.style.cursor = 'pointer';
        g.onclick = () => {
          void api.selectTerritory(sel === i ? null : i, { source: 'user' });
        };
        g.onmouseenter = () => {
          void api.highlightTerritory(i, { source: 'user' });
        };
        g.onmouseleave = () => {
          void api.highlightTerritory(null, { source: 'user' });
        };
      }
      els.push(e);
    }
    sel = -1;
    hl.clear();
    fo = { f: [], dim: 0 };
  }

  const cur: RingState = {
    rotation: cfg.rotation,
    outerR: cfg.outerR,
    thickness: cfg.thickness,
    gap: cfg.gap,
    innerFrac: cfg.innerFrac,
    opacity: 1,
    scale: 1,
    t: Array.from({ length: cfg.segments }, (_, i) => ({
      ...defT(),
      ...norm(cfg.territories[i] || {}),
    })),
  };
  build(cfg.segments);

  const geom = () => {
    const { rotation: rot, outerR: R, thickness: T, gap, innerFrac: f } = cur;
    const n = cur.t.length;
    const rI = R - T;
    const rM = rI + T * f;
    const rm = (rI + R) / 2;
    const gd = gap / rM / RAD;
    const st = 360 / n;
    return { rot, R, T, f, n, rI, rM, rm, gd, st };
  };

  function render() {
    const { rot, R, T, f, n, rI, rM, rm, gd, st } = geom();
    root.setAttribute('transform', `translate(${c} ${c}) scale(${cur.scale}) translate(${-c} ${-c})`);
    root.style.opacity = String(cur.opacity);
    root.style.pointerEvents = cur.opacity < 0.05 ? 'none' : '';
    cur.t.forEach((s, i) => {
      const e = els[i];
      if (!e) return;
      const a0 = rot + i * st + gd / 2;
      const a1 = rot + (i + 1) * st - gd / 2;
      const fc = fo.f[i] || 0;
      const op = s.op * (1 - fo.dim * cfg.dim * (1 - fc));
      const sm = s.inner === s.outer ? 0 : 0.4;
      e.in.setAttribute('d', sectorPath(c, rI, rM - sm, a0, a1));
      e.out.setAttribute('d', sectorPath(c, rM + sm, R, a0, a1));
      for (const [k, col] of [['in', s.inner], ['out', s.outer]] as const) {
        e[k].setAttribute('fill', col);
        e[k].setAttribute('stroke', col);
        e[k].setAttribute('opacity', String(op));
      }
      e.ol.setAttribute('d', sectorPath(c, rI, R, a0, a1));
      e.ol.setAttribute('stroke', sel === i ? cfg.accent : cfg.outline);
      e.ol.setAttribute('stroke-width', String(2 * fc));
      e.ol.setAttribute('opacity', String(fc * s.op));
      const m = cfg.labels;
      const txt = m === 'index' ? i + 1 : m === 'value' ? cfg.fmt(s.value, i) : m === 'label' ? s.label : '';
      const fs = Math.min(14, T * 0.42, ((2 * Math.PI * rm) / n) * 0.38);
      const [x, y] = pt(c, rm, rot + (i + 0.5) * st);
      e.tx.textContent = fs >= 6 ? String(txt) : '';
      e.tx.setAttribute('x', String(x));
      e.tx.setAttribute('y', String(y));
      e.tx.setAttribute('font-size', String(fs));
      e.tx.setAttribute('opacity', String(op));
      e.tx.setAttribute('fill', luminance(f > 0.5 ? s.inner : s.outer) > 0.6 ? '#1d1d1f' : '#fff');
    });
  }

  const animateTo = async (
    next: RingTarget,
    { duration = 700, ease, delay = 0, stagger = 0 }: AnimateOptions = {},
  ) => {
    let structural = false;
    if (next.segments && next.segments !== cur.t.length) {
      structural = true;
      cur.t = Array.from({ length: next.segments }, () => ({ ...defT(), op: 0 }));
      build(next.segments);
    }
    if (next.labels) cfg.labels = next.labels;
    if (next.rotation != null) goalRot = next.rotation;
    const from = cloneRing(cur);
    const to = cloneRing(cur);
    RING_NUM.forEach((k) => {
      const v = next[k];
      if (v != null) to[k] = v;
    });
    if (structural) to.t.forEach(s => (s.op = 1));
    (next.territories || []).forEach((p, i) => to.t[i] && Object.assign(to.t[i], norm(p)));
    return Anim.tween({
      duration,
      ease,
      delay,
      owner: api,
      key: 'state',
      onUpdate: (p) => {
        RING_NUM.forEach(k => (cur[k] = lerp(from[k], to[k], p)));
        cur.t = to.t.map((s, i) => {
          const fr = from.t[i];
          const q = stagger ? clamp((p - (stagger * i) / to.t.length) / (1 - stagger)) : p;
          return {
            inner: mix(fr.inner, s.inner, q),
            outer: mix(fr.outer, s.outer, q),
            op: lerp(fr.op, s.op, q),
            value: lerp(fr.value, s.value, q),
            label: s.label,
          };
        });
        render();
      },
    });
  };

  async function focus(o?: { duration?: number }) {
    const tgt = els.map((_, i) => (hl.has(i) || i === sel ? 1 : 0));
    const any = hl.size || sel >= 0 ? 1 : 0;
    const f0 = [...fo.f];
    const d0 = fo.dim;
    return Anim.tween({
      duration: o?.duration ?? 220,
      ease: 'out',
      owner: api,
      key: 'focus',
      onUpdate: (p) => {
        fo.f = tgt.map((v, i) => lerp(f0[i] || 0, v, p));
        fo.dim = lerp(d0, any, p);
        render();
      },
    });
  }
  const info = (i: number | null) => (i == null || i < 0 ? null : { index: i, ...cur.t[i] });

  const highlightTerritory = async (
    i: number | number[] | null,
    o: { source?: string; duration?: number } = {},
  ) => {
    hl = new Set(i == null ? [] : ([] as number[]).concat(i));
    api.emit('highlight', { index: i, source: o.source || 'api' });
    return focus(o);
  };
  const selectTerritory = async (
    i: number | null,
    o: { source?: string; duration?: number } = {},
  ) => {
    const n = i ?? -1;
    if (n === sel) return Promise.resolve();
    sel = n;
    const p = focus(o);
    api.emit('select', { index: i ?? null, territory: info(n), source: o.source || 'api' });
    return p;
  };

  /** Sprinkle "pixels" inside territory i, then collapse them into its centre (median sampling motion). */
  const scatter = async (indices: number | number[], o: ScatterOptions = {}) => {
    const list = ([] as number[]).concat(indices);
    const count = o.count ?? 22;
    const { rot, R, rI, gd, st } = geom();
    const dots: { el: SVGCircleElement; x: number; y: number; tx: number; ty: number }[] = [];
    list.forEach((i) => {
      const [mx, my] = pt(c, (rI + R) / 2, rot + (i + 0.5) * st);
      for (let k = 0; k < count; k++) {
        const a = rot + i * st + gd / 2 + Math.random() * (st - gd);
        const r = rI + 3 + Math.random() * (R - rI - 6);
        const [x, y] = pt(c, r, a);
        const g = Math.round(60 + Math.random() * 180);
        const col = o.color ? o.color(k) : `rgb(${g},${g},${g})`;
        const el = svgEl('circle', { cx: x, cy: y, r: o.radius ?? 2.6, fill: col, opacity: 0 }, gX);
        dots.push({ el, x, y, tx: mx, ty: my });
      }
    });
    const n = dots.length;
    return Anim.tween({
      duration: o.appear ?? 500,
      ease: 'out',
      onUpdate: p => dots.forEach((d, k) => d.el.setAttribute('opacity', String(clamp(p * 2 - k / n)))),
    })
      .then(async () => Anim.wait(o.hold ?? 250))
      .then(async () =>
        Anim.tween({
          duration: o.collapse ?? 550,
          ease: 'in',
          onUpdate: (p) => {
            dots.forEach((d) => {
              d.el.setAttribute('cx', String(lerp(d.x, d.tx, p)));
              d.el.setAttribute('cy', String(lerp(d.y, d.ty, p)));
              d.el.setAttribute('opacity', String(1 - p * 0.9));
            });
          },
        }),
      )
      .then(() => dots.forEach(d => d.el.remove()));
  };

  const out = Object.assign(api, {
    animateTo,
    updateData: async (territories: TerritoryInput[], o?: AnimateOptions) =>
      animateTo({ territories }, { duration: 0, ...o }),
    rotate: async (by: number, o?: AnimateOptions) => animateTo({ rotation: goalRot + by }, o),
    rotateTo: async (deg: number, o?: AnimateOptions) => animateTo({ rotation: deg }, o),
    show: async (o?: AnimateOptions) => animateTo({ opacity: 1, scale: 1 }, o),
    hide: async (o?: AnimateOptions) => animateTo({ opacity: 0, scale: 0.9 }, o),
    highlightTerritory,
    selectTerritory,
    scatter,
    getData: () => cur.t.map((t, i) => ({ index: i, ...t })),
    getSelection: () => (sel < 0 ? null : sel),
    setCenter: (t: string) => {
      ctr.textContent = t;
    },
    anchor: (i: number, w: 'mid' | 'outer' | 'inner' = 'mid') => {
      const n = cur.t.length;
      const r = w === 'outer' ? cur.outerR : w === 'inner' ? cur.outerR - cur.thickness : cur.outerR - cur.thickness / 2;
      const [x, y] = pt(c, r, cur.rotation + ((i + 0.5) * 360) / n);
      const b = svg.getBoundingClientRect();
      const k = b.width / cfg.size;
      return { x: b.left + (c + (x - c) * cur.scale) * k, y: b.top + (c + (y - c) * cur.scale) * k };
    },
    destroy: () => svg.remove(),
  });
  render();
  return out;
}

/* ---------- Graph ---------- */
const ticks = (a: number, b: number, n: number) => {
  const r = (b - a) / n;
  const p = 10 ** Math.floor(Math.log10(r));
  const s = ([1, 2, 5, 10].find(m => m * p >= r) || 10) * p;
  const o: number[] = [];
  for (let v = Math.ceil(a / s - 1e-9) * s; v <= b + 1e-9; v += s) o.push(+v.toFixed(10));
  return o;
};
type Pt = [number, number];
const samp = (a: Pt[], u: number): Pt => {
  if (a.length < 2) return a[0];
  const f = u * (a.length - 1);
  const i = Math.min(a.length - 2, Math.floor(f));
  const t = f - i;
  return [lerp(a[i][0], a[i + 1][0], t), lerp(a[i][1], a[i + 1][1], t)];
};
let uid = 0;

export interface SeriesOptions {
  color?: string;
  width?: number;
  dash?: string;
  label?: string;
  reveal?: number;
  duration?: number;
  ease?: EaseName;
}
export interface GraphOptions {
  width?: number;
  height?: number;
  m?: { t: number; r: number; b: number; l: number };
  x?: [number, number];
  y?: [number, number];
  xLabel?: string;
  yLabel?: string;
  xTicks?: number;
  yTicks?: number;
  fmtX?: (v: number) => string | number;
  fmtY?: (v: number) => string | number;
  fmtMk?: (x: number, y: number) => string;
  accent?: string;
  scrub?: boolean;
  series?: (SeriesOptions & { id: string; data: Pt[] })[];
}

export type Graph = ReturnType<typeof createGraph>;

/**
 * Line graph with morphing datasets, progressive drawing, regions and a curve-following marker.
 * CSS hooks: .cv-graph text, .cv-graph .g (grid), .cv-graph .ax (axis), .cv-graph .cv-mk-text
 */
export function createGraph(host: HTMLElement, o: GraphOptions = {}) {
  const cfg = {
    width: 640,
    height: 220,
    m: { t: 12, r: 16, b: 36, l: 48 },
    x: [0, 1] as [number, number],
    y: [0, 1] as [number, number],
    xLabel: '',
    yLabel: '',
    xTicks: 6,
    yTicks: 4,
    fmtX: (v: number) => v,
    fmtY: (v: number) => v,
    accent: ACCENT,
    scrub: false,
    series: [] as NonNullable<GraphOptions['series']>,
    ...o,
  } as Required<Omit<GraphOptions, 'fmtMk'>> & { fmtMk?: GraphOptions['fmtMk'] };
  cfg.fmtMk ??= (x, y) => `${cfg.fmtX(+x.toFixed(2))}, ${cfg.fmtY(y)}`;
  const api = emitter({});
  const m = cfg.m;
  const iw = cfg.width - m.l - m.r;
  const ih = cfg.height - m.t - m.b;
  const id = `cv-clip-${++uid}`;
  const svg = svgEl('svg', { viewBox: `0 0 ${cfg.width} ${cfg.height}`, class: 'cv-graph' });
  svg.style.cssText = 'display:block;width:100%;height:auto';
  host.append(svg);
  svgEl('clipPath', { id }, svgEl('defs', {}, svg)).append(
    svgEl('rect', { x: m.l - 2, y: m.t - 8, width: iw + 4, height: ih + 10 }),
  );
  const gR = svgEl('g', {}, svg);
  const gA = svgEl('g', {}, svg);
  const gC = svgEl('g', { 'clip-path': `url(#${id})` }, svg);
  const gLab = svgEl('g', {}, svg);
  const gP = svgEl('g', {}, svg);
  const mG = svgEl('g', {}, svg);
  const mL = svgEl('line', { 'stroke': cfg.accent, 'stroke-dasharray': '3 3', 'stroke-width': 1 }, mG);
  const mD = svgEl('circle', { 'r': 3.5, 'fill': '#fff', 'stroke': cfg.accent, 'stroke-width': 2.5 }, mG);
  const mT = svgEl('text', { 'text-anchor': 'middle', 'class': 'cv-mk-text' }, mG);

  interface Series {
    color: string;
    dash?: string;
    pts: Pt[];
    reveal: number;
    hi: number[];
    hv: number;
    path: SVGPathElement;
    lab?: SVGTextElement;
  }
  interface Region {
    x?: [number, number];
    y?: [number, number];
    label?: string;
    color?: string;
    opacity?: number;
    v: number;
    rect: SVGRectElement;
    tx: SVGTextElement | null;
  }
  const S: Record<string, Series> = {};
  const RG: Region[] = [];
  const mk = { x: null as number | null, v: 0, id: null as string | null };
  let dom = { x: [...cfg.x] as Pt, y: [...cfg.y] as Pt };
  const sx = (v: number) => m.l + ((v - dom.x[0]) / (dom.x[1] - dom.x[0])) * iw;
  const sy = (v: number) => m.t + ih - ((v - dom.y[0]) / (dom.y[1] - dom.y[0])) * ih;
  const valueAt = (sid: string, x: number) => {
    const p = (S[sid] || { pts: [] }).pts;
    if (!p.length) return 0;
    let i = 0;
    while (i < p.length - 2 && p[i + 1][0] < x) i++;
    const a = p[i];
    const b = p[i + 1] || a;
    return b[0] === a[0] ? a[1] : lerp(a[1], b[1], clamp((x - a[0]) / (b[0] - a[0])));
  };

  function axes() {
    gA.replaceChildren();
    const txt = (x: number, y: number, a: string, t: string | number, e: Attrs = {}) => {
      svgEl('text', { x, y, 'text-anchor': a, ...e }, gA).textContent = String(t);
    };
    ticks(dom.y[0], dom.y[1], cfg.yTicks).forEach((v) => {
      const y = sy(v);
      svgEl('line', { x1: m.l, x2: m.l + iw, y1: y, y2: y, class: 'g' }, gA);
      txt(m.l - 8, y, 'end', cfg.fmtY(v), { 'dominant-baseline': 'central' });
    });
    ticks(dom.x[0], dom.x[1], cfg.xTicks).forEach(v => txt(sx(v), m.t + ih + 16, 'middle', cfg.fmtX(v)));
    svgEl('line', { x1: m.l, x2: m.l + iw, y1: m.t + ih, y2: m.t + ih, class: 'ax' }, gA);
    txt(m.l + iw / 2, cfg.height - 4, 'middle', cfg.xLabel);
    txt(12, m.t + ih / 2, 'middle', cfg.yLabel, { transform: `rotate(-90 12 ${m.t + ih / 2})` });
  }

  function paint() {
    for (const s of Object.values(S)) {
      s.path.setAttribute(
        'd',
        s.pts.length ? `M${s.pts.map(p => `${sx(p[0]).toFixed(1)} ${sy(p[1]).toFixed(1)}`).join('L')}` : '',
      );
      if (s.dash) s.path.style.opacity = String(s.reveal);
      else s.path.style.strokeDasharray = `${s.reveal} 1.001`;
      if (s.lab && s.pts.length) {
        const l = s.pts[s.pts.length - 1];
        s.lab.setAttribute('x', String(sx(l[0]) + 6));
        s.lab.setAttribute('y', String(sy(l[1])));
        s.lab.style.opacity = String(s.reveal);
      }
    }
    gP.replaceChildren();
    for (const s of Object.values(S)) {
      if (s.hv > 0.01) {
        s.hi.forEach((i) => {
          const p = s.pts[i];
          if (p) svgEl('circle', { 'cx': sx(p[0]), 'cy': sy(p[1]), 'r': 4.5 * s.hv, 'fill': '#fff', 'stroke': s.color, 'stroke-width': 2 }, gP);
        });
      }
    }
    RG.forEach((r) => {
      const [a, b] = (r.x || r.y)!;
      const X = r.x ? [sx(a), sx(b)] : [m.l, m.l + iw];
      const Y = r.x ? [m.t, m.t + ih] : [sy(b), sy(a)];
      r.rect.setAttribute('x', String(X[0]));
      r.rect.setAttribute('y', String(Y[0]));
      r.rect.setAttribute('width', String(Math.max(0, X[1] - X[0])));
      r.rect.setAttribute('height', String(Math.max(0, Y[1] - Y[0])));
      r.rect.setAttribute('opacity', String(r.v * (r.opacity ?? 0.1)));
      if (r.tx) {
        r.tx.setAttribute('x', String(X[0] + 6));
        r.tx.setAttribute('y', String(Y[0] + 13));
        r.tx.style.opacity = String(r.v);
      }
    });
    if (mk.x != null && mk.id && S[mk.id]) {
      const y = valueAt(mk.id, mk.x);
      const X = sx(mk.x);
      const Y = sy(y);
      mG.style.opacity = String(mk.v);
      mL.setAttribute('x1', String(X));
      mL.setAttribute('x2', String(X));
      mL.setAttribute('y1', String(Y));
      mL.setAttribute('y2', String(m.t + ih));
      mD.setAttribute('cx', String(X));
      mD.setAttribute('cy', String(Y));
      mT.setAttribute('x', String(X));
      mT.setAttribute('y', String(Y - 12));
      mT.setAttribute('text-anchor', X > cfg.width * 0.8 ? 'end' : X < cfg.width * 0.2 ? 'start' : 'middle');
      mT.textContent = cfg.fmtMk!(mk.x, y);
    }
    else {
      mG.style.opacity = '0';
    }
  }

  const setData = async (sid: string, data: Pt[], so: SeriesOptions = {}) => {
    let s = S[sid];
    if (!s) {
      s = S[sid] = {
        color: so.color || cfg.accent,
        dash: so.dash,
        pts: [],
        reveal: so.reveal ?? 1,
        hi: [],
        hv: 0,
        path: svgEl('path', {
          'fill': 'none',
          'stroke-linejoin': 'round',
          'stroke-linecap': 'round',
          'pathLength': 1,
          'stroke': so.color || cfg.accent,
          'stroke-width': so.width || 2,
        }, gC),
      };
      if (so.dash) s.path.setAttribute('stroke-dasharray', so.dash);
      if (so.label) {
        s.lab = svgEl('text', { 'text-anchor': 'start', 'dominant-baseline': 'central', 'class': 'cv-series-label' }, gLab);
        s.lab.textContent = so.label;
        s.lab.style.fill = s.color;
      }
    }
    const series = s;
    const to = data.map(p => [...p] as Pt);
    if (!series.pts.length || !(so.duration && so.duration > 0)) {
      series.pts = to;
      paint();
      return Promise.resolve();
    }
    const from = series.pts.map(p => [...p] as Pt);
    const M = Math.max(from.length, to.length);
    const A = Array.from({ length: M }, (_, k) => samp(from, k / (M - 1)));
    const B = Array.from({ length: M }, (_, k) => samp(to, k / (M - 1)));
    return Anim.tween({
      duration: so.duration,
      ease: so.ease,
      owner: api,
      key: `d${sid}`,
      onUpdate: (p) => {
        series.pts = p >= 1 ? to : A.map((a, k) => [lerp(a[0], B[k][0], p), lerp(a[1], B[k][1], p)] as Pt);
        paint();
      },
    });
  };

  const draw = async (d: { duration?: number; ease?: EaseName; delay?: number; series?: string[]; sequential?: boolean } = {}) => {
    const ids = d.series || Object.keys(S);
    ids.forEach(i => (S[i].reveal = 0));
    paint();
    return Anim.tween({
      duration: d.duration ?? 1200,
      ease: d.ease,
      delay: d.delay,
      owner: api,
      key: `draw${ids.join(',')}`,
      onUpdate: (p) => {
        ids.forEach((i, k) => (S[i].reveal = d.sequential ? clamp(p * ids.length - k) : p));
        paint();
      },
    });
  };

  if (cfg.scrub) {
    let down = false;
    svg.style.touchAction = 'none';
    svg.style.cursor = 'ew-resize';
    const sc = (e: PointerEvent) => {
      const b = svg.getBoundingClientRect();
      const px = ((e.clientX - b.left) / b.width) * cfg.width;
      api.emit('scrub', { x: clamp(dom.x[0] + ((px - m.l) / iw) * (dom.x[1] - dom.x[0]), dom.x[0], dom.x[1]) });
    };
    svg.onpointerdown = (e) => {
      down = true;
      svg.setPointerCapture(e.pointerId);
      sc(e);
    };
    svg.onpointermove = e => down && sc(e);
    svg.onpointerup = () => (down = false);
  }

  const out = Object.assign(api, {
    setData,
    draw,
    hideCurves: (ids?: string[]) => {
      (ids || Object.keys(S)).forEach((i) => {
        S[i].reveal = 0;
        S[i].hv = 0;
      });
      paint();
    },
    highlightPoints: async (sid: string, idx: number[], ho: { duration?: number } = {}) => {
      const s = S[sid];
      const v0 = s.hv;
      if (idx.length) s.hi = idx;
      return Anim.tween({
        duration: ho.duration ?? 300,
        ease: 'out',
        owner: api,
        key: `hp${sid}`,
        onUpdate: (p) => {
          s.hv = lerp(v0, idx.length ? 1 : 0, p);
          paint();
        },
      }).then(() => {
        if (!idx.length) s.hi = [];
      });
    },
    addRegion: (def: { x?: [number, number]; y?: [number, number]; label?: string; color?: string; opacity?: number }, ro: { duration?: number } = {}) => {
      const r: Region = {
        ...def,
        v: 0,
        rect: svgEl('rect', { fill: def.color || cfg.accent }, gR),
        tx: def.label ? svgEl('text', {}, gR) : null,
      };
      if (r.tx) r.tx.textContent = def.label!;
      RG.push(r);
      void Anim.tween({ duration: ro.duration ?? 400, ease: 'out', onUpdate: (p) => {
        r.v = p;
        paint();
      } });
      return r;
    },
    clearRegions: () => {
      RG.splice(0).forEach((r) => {
        r.rect.remove();
        r.tx?.remove();
      });
      paint();
    },
    moveMarker: async (x: number | null, mo: { series?: string; duration?: number; ease?: EaseName } = {}) => {
      if (x == null) {
        const v0 = mk.v;
        return Anim.tween({ duration: mo.duration ?? 250, owner: api, key: 'mv', onUpdate: (p) => {
          mk.v = lerp(v0, 0, p);
          paint();
        } });
      }
      mk.id = mo.series || mk.id || Object.keys(S)[0];
      const x0 = mk.x == null || mk.v < 0.05 ? x : mk.x;
      const v0 = mk.v;
      return Anim.tween({ duration: mo.duration ?? 600, ease: mo.ease, owner: api, key: 'mv', onUpdate: (p) => {
        mk.x = lerp(x0, x, p);
        mk.v = lerp(v0, 1, p);
        paint();
      } });
    },
    setDomain: async (d: { x?: Pt; y?: Pt }, dd: { duration?: number } = {}) => {
      const f = { x: [...dom.x] as Pt, y: [...dom.y] as Pt };
      const t = { x: d.x || dom.x, y: d.y || dom.y };
      return Anim.tween({ duration: dd.duration ?? 700, owner: api, key: 'dom', onUpdate: (p) => {
        dom = { x: f.x.map((v, i) => lerp(v, t.x[i], p)) as Pt, y: f.y.map((v, i) => lerp(v, t.y[i], p)) as Pt };
        axes();
        paint();
      } });
    },
    /** Update axis labels / formatters without rebuilding the graph. */
    configure: (c: Partial<Pick<GraphOptions, 'xLabel' | 'yLabel' | 'fmtX' | 'fmtY' | 'fmtMk' | 'yTicks' | 'xTicks'>>) => {
      Object.assign(cfg, c);
      axes();
      paint();
    },
    setSeriesLabel: (sid: string, text: string) => {
      if (S[sid]?.lab) S[sid].lab.textContent = text;
    },
    /** Page coordinates of the curve point at x. */
    point: (x: number, sid?: string) => {
      const b = svg.getBoundingClientRect();
      const k = b.width / cfg.width;
      return { x: b.left + sx(x) * k, y: b.top + sy(valueAt(sid || mk.id || '', x)) * k };
    },
    destroy: () => svg.remove(),
  });
  cfg.series.forEach((d) => {
    void setData(d.id, d.data, d);
  });
  axes();
  paint();
  return out;
}

/* ---------- Overlay: animated links between arbitrary anchors (ring<->ring, graph<->ring) ---------- */
export interface LinkOptions {
  from: () => { x: number; y: number };
  to: () => { x: number; y: number };
  dir?: 'h' | 'v';
  color?: string;
  width?: number;
  opacity?: number;
  reveal?: number;
}
export function createOverlay(host: HTMLElement) {
  if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
  const svg = svgEl('svg', { class: 'cv-overlay' });
  svg.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:visible';
  host.append(svg);
  interface Link {
    set: (s: Partial<CSSStyleDeclaration>) => void;
    show: (x?: TweenOptions) => Promise<void>;
    hide: (x?: TweenOptions) => Promise<void>;
    update: (b?: DOMRect) => void;
    destroy: () => void;
  }
  const L = new Set<Link>();
  const upd = () => {
    const b = svg.getBoundingClientRect();
    L.forEach(l => l.update(b));
  };
  const unsub = Anim.afterTick(upd);
  addEventListener('resize', upd);
  const link = (o: LinkOptions) => {
    const p = svgEl('path', { 'fill': 'none', 'stroke-linecap': 'round', 'pathLength': 1, 'stroke': o.color || '#8e8e93', 'stroke-width': o.width || 1 }, svg);
    p.style.opacity = String(o.opacity ?? 1);
    let rv = o.reveal ?? 1;
    let l: Link;
    const go = async (to: number, x: TweenOptions = {}) => {
      const r0 = rv;
      return Anim.tween({ duration: 600, ...x, owner: l, key: 'rv', onUpdate: (q) => {
        rv = lerp(r0, to, q);
      } });
    };
    l = {
      set: s => Object.assign(p.style, s),
      show: async x => go(1, x),
      hide: async x => go(0, { duration: 400, ...x }),
      update(b = svg.getBoundingClientRect()) {
        const a = o.from();
        const z = o.to();
        const ax = a.x - b.left;
        const ay = a.y - b.top;
        const bx = z.x - b.left;
        const by = z.y - b.top;
        p.setAttribute('d', o.dir === 'v'
          ? `M${ax} ${ay}C${ax} ${(ay + by) / 2} ${bx} ${(ay + by) / 2} ${bx} ${by}`
          : `M${ax} ${ay}C${(ax + bx) / 2} ${ay} ${(ax + bx) / 2} ${by} ${bx} ${by}`);
        p.style.strokeDasharray = `${rv} 1.001`;
      },
      destroy: () => {
        L.delete(l);
        p.remove();
      },
    };
    L.add(l);
    return l;
  };
  requestAnimationFrame(upd);
  return {
    svg,
    link,
    update: upd,
    destroy: () => {
      unsub();
      removeEventListener('resize', upd);
      svg.remove();
    },
  };
}
